// @ts-check

/**
 * @file Theme-aware date-range picker built on our own `haya-date-picker`.
 * Renders a bordered trigger that opens a calendar popup; the user picks a
 * start and end day (month navigation via the calendar's own arrows). Values
 * cross the boundary as `YYYY-MM-DD` strings so screens keep working with the
 * same string filters they already store in route query params.
 *
 * The calendar is left uncontrolled (no `dateFrom`/`dateTo` passed to it) so a
 * fresh range can always be picked with two presses; this component owns the
 * displayed value from its string props instead.
 */

import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import HayaDatePicker from "haya-date-picker/build/index.js"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import Text from "@kaspernj/api-maker/build/utils/text"
import {memo} from "react"
import {Pressable, View} from "react-native-propforge"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"

import Modal from "./modal"
import {FORM_CONTROL_BORDER_RADIUS, FORM_CONTROL_BORDER_WIDTH, FORM_CONTROL_FONT_SIZE, FORM_CONTROL_LINE_HEIGHT, FORM_CONTROL_PADDING_HORIZONTAL, FORM_CONTROL_PADDING_VERTICAL} from "../shared/form-control-metrics"
import useThemeColors, {useThemeName} from "../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} DateRangePickerProps
 * @property {string} dateFrom Start date as `YYYY-MM-DD` (or empty).
 * @property {string} dateTo End date as `YYYY-MM-DD` (or empty).
 * @property {(value: {dateFrom: string, dateTo: string}) => unknown} onChange Range change handler.
 * @property {string} testID Stable test id wrapping the trigger.
 */

/** @typedef {{open: boolean}} DateRangePickerState */

/**
 * Parses a `YYYY-MM-DD` string into a local-time `Date`.
 * @param {string} value Date string to parse.
 * @returns {Date | undefined} Parsed date, or undefined when empty/invalid.
 */
function parseDateString(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined
  const [year, month, day] = value.split("-").map((part) => Number.parseInt(part, 10))
  const date = new Date(year, month - 1, day)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return undefined
  return date
}

/**
 * Formats a local-time `Date` into a `YYYY-MM-DD` string.
 * @param {Date} date Date to format.
 * @returns {string} Zero-padded date string.
 */
function formatDateString(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
}

/**
 * Shared theme-aware range picker. Renders a themed trigger plus a calendar
 * popup and translates between `YYYY-MM-DD` strings and the `Date` objects the
 * calendar works with.
 * @augments {ShapeComponent<DateRangePickerProps, DateRangePickerState>}
 */
class DateRangePicker extends ShapeComponent {
  static propTypes = propTypesExact({
    dateFrom: PropTypes.string.isRequired,
    dateTo: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    testID: PropTypes.string.isRequired
  })

  /** @type {DateRangePickerState} */
  state = {open: false}
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires the active theme palette into the instance. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the trigger and calendar popup.
   * @returns {ReactNode} Range picker content.
   */
  render() {
    const {dateFrom, dateTo, testID} = this.p
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const hasRange = Boolean(dateFrom) && Boolean(dateTo)
    return (
      <View testID={testID}>
        <Pressable
          onPress={this.tt.onOpen}
          style={styles[`trigger-${themeName}`] ||= {
            alignItems: "center",
            backgroundColor: colors.surface,
            borderColor: colors.borderStrong,
            borderRadius: FORM_CONTROL_BORDER_RADIUS,
            borderWidth: FORM_CONTROL_BORDER_WIDTH,
            flexDirection: "row",
            gap: 8,
            paddingHorizontal: FORM_CONTROL_PADDING_HORIZONTAL,
            paddingVertical: FORM_CONTROL_PADDING_VERTICAL
          }}
          testID={`${testID}/trigger`}
        >
          <FontAwesome6 color={colors.textMuted} name="calendar-days" size={14} />
          <Text
            style={hasRange
              ? styles[`value-${themeName}`] ||= {
                color: colors.text,
                fontSize: FORM_CONTROL_FONT_SIZE,
                lineHeight: FORM_CONTROL_LINE_HEIGHT
              }
              : styles[`placeholder-${themeName}`] ||= {
                color: colors.placeholder,
                fontSize: FORM_CONTROL_FONT_SIZE,
                lineHeight: FORM_CONTROL_LINE_HEIGHT
              }
            }
            testID={`${testID}/value`}
          >
            {hasRange ? `${dateFrom} - ${dateTo}` : "YYYY-MM-DD - YYYY-MM-DD"}
          </Text>
        </Pressable>
        <Modal
          cardStyle={styles.card ||= {
            maxWidth: 360,
            padding: 12
          }}
          isVisible={this.s.open}
          onRequestClose={this.tt.onClose}
          testID={`${testID}/modal`}
        >
          <View
            style={styles.calendarSurface ||= {
              backgroundColor: "#ffffff",
              borderRadius: 12,
              paddingVertical: 12
            }}
            testID={`${testID}/calendar`}
          >
            <HayaDatePicker
              defaultCurrentDate={parseDateString(dateFrom) || new Date()}
              mode="dateRange"
              onSelect={this.tt.onSelect}
              showWeekNumbers={false}
            />
          </View>
        </Modal>
      </View>
    )
  }

  /**
   * Opens the calendar popup.
   * @returns {void} Nothing.
   */
  onOpen = () => {
    this.s.open = true
  }

  /**
   * Closes the calendar popup.
   * @returns {void} Nothing.
   */
  onClose = () => {
    this.s.open = false
  }

  /**
   * Maps the calendar's `{fromDate, toDate}` selection back to string filters.
   * @param {{fromDate?: Date, toDate?: Date}} selection Selected range endpoints.
   * @returns {void} Nothing.
   */
  onSelect = (selection) => {
    const {fromDate, toDate} = selection
    this.s.open = false
    if (!fromDate || !toDate) return
    this.p.onChange({
      dateFrom: formatDateString(fromDate),
      dateTo: formatDateString(toDate)
    })
  }
}

/** @type {NamedExoticComponent<DateRangePickerProps>} */
const DateRangePickerComponent = memo(shapeComponent(DateRangePicker))

export default DateRangePickerComponent
